import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { resolveTenant, type TenantResolution } from "../lib/tenantResolver";
import { storefrontDevLog } from "../lib/devLog";
import { StorefrontApiError } from "../services/apiClient";
import { storefrontApi } from "../services/storefrontApi";
import type {
  StoreDirectoryEntry,
  StorefrontBranch,
  StorefrontCategory,
  StorefrontConfig,
  ThemeId
} from "../types/storefront";

export type StorefrontViewState =
  | "directory"
  | "loading"
  | "ready"
  | "tenant-not-found"
  | "storefront-disabled"
  | "tenant-unavailable"
  | "api-error"
  | "invalid-tenant";

export interface StorefrontState {
  resolution: TenantResolution;
  viewState: StorefrontViewState;
  slug: string | null;
  store: StorefrontConfig | null;
  stores: StoreDirectoryEntry[];
  branches: StorefrontBranch[];
  categories: StorefrontCategory[];
  loading: boolean;
  error: string | null;
  previewTheme?: ThemeId;
  setPreviewTheme: (theme?: ThemeId) => void;
  retry: () => void;
}

const StorefrontContext = createContext<StorefrontState | null>(null);
const STORE_STORAGE_KEY = "elmor-current-store";

const readStorage = (storage: Storage) => {
  try {
    return storage.getItem(STORE_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStorage = (storage: Storage, slug: string) => {
  try {
    storage.setItem(STORE_STORAGE_KEY, slug);
  } catch {
    // Storage can be unavailable in privacy modes; the in-memory context still remains valid.
  }
};

export function StorefrontProvider({ children }: { children: React.ReactNode }) {
  const resolution = useMemo(
    () => resolveTenant({
      hostname: window.location.hostname,
      search: window.location.search,
      rootDomain: import.meta.env.VITE_ROOT_DOMAIN,
      sessionSlug: readStorage(window.sessionStorage),
      localSlug: readStorage(window.localStorage),
      fallbackSlug: import.meta.env.VITE_DEFAULT_STORE_SLUG
    }),
    []
  );
  const [store, setStore] = useState<StorefrontConfig | null>(null);
  const [stores, setStores] = useState<StoreDirectoryEntry[]>([]);
  const [branches, setBranches] = useState<StorefrontBranch[]>([]);
  const [categories, setCategories] = useState<StorefrontCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewState, setViewState] = useState<StorefrontViewState>(
    resolution.mode === "directory" ? "directory" : resolution.mode === "invalid" ? "invalid-tenant" : "loading"
  );
  const [retryKey, setRetryKey] = useState(0);
  const [previewTheme, setPreviewTheme] = useState<ThemeId>();

  useEffect(() => {
    if (resolution.mode !== "tenant") return;
    writeStorage(window.sessionStorage, resolution.slug);
    writeStorage(window.localStorage, resolution.slug);
    storefrontDevLog("Resolved tenant", {
      slug: resolution.slug,
      source: resolution.source,
      apiUrl: import.meta.env.VITE_STOREFRONT_API_URL || "http://localhost:5000/api/storefront"
    });
  }, [resolution]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setError(null);
      setStore(null);
      setBranches([]);
      setCategories([]);

      if (resolution.mode === "invalid") {
        setViewState("invalid-tenant");
        setLoading(false);
        return;
      }

      setLoading(true);
      if (resolution.mode === "directory") {
        setViewState("directory");
        try {
          const nextStores = await storefrontApi.getPublicStores();
          if (active) setStores(nextStores);
        } catch (loadError) {
          if (active) {
            setStores([]);
            setError(loadError instanceof Error ? loadError.message : "The store directory is unavailable.");
          }
        } finally {
          if (active) setLoading(false);
        }
        return;
      }

      setViewState("loading");
      try {
        const [nextStore, nextBranches] = await Promise.all([
          storefrontApi.getStorefrontConfig(resolution.slug),
          storefrontApi.getStorefrontBranches(resolution.slug)
        ]);
        if (!active) return;
        if (!nextStore) {
          setViewState("tenant-not-found");
          return;
        }
        if (!nextStore.storefrontEnabled) {
          setViewState("storefront-disabled");
          return;
        }
        const nextCategories = await storefrontApi.getStorefrontCategories(resolution.slug, nextBranches[0]?.id);
        if (!active) return;
        setStore(nextStore);
        setBranches(nextBranches);
        setCategories(nextCategories);
        setViewState("ready");
        storefrontDevLog("Loaded storefront context", {
          tenant: resolution.slug,
          branches: nextBranches.length,
          categories: nextCategories.length
        });
      } catch (loadError) {
        if (!active) return;
        const status = loadError instanceof StorefrontApiError ? loadError.status : undefined;
        const message = loadError instanceof Error ? loadError.message : "The store service is unavailable.";
        setError(message);
        if (status === 404) setViewState("tenant-not-found");
        else if (status === 403 && /disabled/i.test(message)) setViewState("storefront-disabled");
        else if (status === 403 || status === 410) setViewState("tenant-unavailable");
        else setViewState("api-error");
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [resolution, retryKey]);

  const retry = useCallback(() => setRetryKey((value) => value + 1), []);
  const value = useMemo<StorefrontState>(
    () => ({
      resolution,
      viewState,
      slug: resolution.slug,
      store,
      stores,
      branches,
      categories,
      loading,
      error,
      previewTheme,
      setPreviewTheme,
      retry
    }),
    [resolution, viewState, store, stores, branches, categories, loading, error, previewTheme, retry]
  );

  return <StorefrontContext.Provider value={value}>{children}</StorefrontContext.Provider>;
}

export function useStorefront() {
  const value = useContext(StorefrontContext);
  if (!value) throw new Error("useStorefront must be used inside StorefrontProvider.");
  return value;
}
